import { queryNullChildren } from 'constants/search'
import { encodeChildrenOccupancy } from 'lib/search/searchUtils'
import { parseAmount } from './ghaPriceGap'
import { getPartnerOfferId } from './ghaLoadingDiagnostics'

export interface MetasearchPriceBreakdown {
  propertyId: string
  rateId: string | undefined
  supplier: string | undefined
  checkIn: string | undefined
  checkOut: string | undefined
  rooms: Array<App.Occupants>
  /** Calculated public display price, including per-person pricing, before the verifier override. */
  total: number
  /** Whole-stay amounts supplied to the price-details modal. */
  taxesAndFees: number
  propertyFees: number
}

/** Compare the original GHA itinerary only; URL values cease to apply after a guest edits it. */
export function getMetasearchPriceBreakdownCheck(
  query: Record<string, unknown>,
  pricing: MetasearchPriceBreakdown,
  currency: string,
  pathname = '',
) {
  const sourceTotal = parseAmount(query.sourceTotal)
  const sourceTax = parseAmount(query.sourceTax)
  const sourceFees = parseAmount(query.sourceFees)
  const context = {
    sourceTotal,
    sourceTax,
    sourceFees,
    sourceTotalPresent: typeof query.sourceTotal === 'string' && !!query.sourceTotal.trim(),
    sourceTaxPresent: typeof query.sourceTax === 'string' && !!query.sourceTax.trim(),
    sourceFeesPresent: typeof query.sourceFees === 'string' && !!query.sourceFees.trim(),
    sourceCurrency: typeof query.currency === 'string' ? query.currency : undefined,
    currency,
    propertyId: pricing.propertyId,
    rateId: pricing.rateId,
    supplier: pricing.supplier,
    checkIn: pricing.checkIn,
    checkOut: pricing.checkOut,
    portalTotal: pricing.total,
    portalTaxesAndFees: pricing.taxesAndFees,
    portalPropertyFees: pricing.propertyFees,
  }

  if (sourceTotal === undefined || sourceTotal <= 0 || sourceTax === undefined || sourceTax < 0 || sourceFees === undefined || sourceFees < 0) {
    return { ...context, result: 'invalid_source' }
  }
  if (!query.currency || query.currency !== currency) {
    return { ...context, result: 'currency_mismatch' }
  }
  const adults = pricing.rooms.map(room => room.adults).join(',')
  const children = pricing.rooms.map(room => encodeChildrenOccupancy(room.childrenAge)).join(',')
  // The primary tile forwards source prices to /partner/:slug/:id, whose property identity is
  // in the path rather than a propertyId query param. Secondary tiles do not forward them.
  const sourcePropertyId = query.propertyId ?? (typeof query.ghaPropertyId === 'string' ?
    `bedbank:${query.ghaPropertyId}` :
    `bedbank:${getPartnerOfferId(pathname)}`)
  const sameProperty = sourcePropertyId === `bedbank:${pricing.propertyId}`
  const sameDates = pricing.checkIn && pricing.checkOut && query.checkIn === pricing.checkIn && query.checkOut === pricing.checkOut
  const sameOccupancy = pricing.rooms.length > 0 && query.adults === adults && (query.children ?? queryNullChildren) === children
  if (!sameProperty || !sameDates || !sameOccupancy) {
    return { ...context, result: 'itinerary_mismatch' }
  }
  if (![pricing.total, pricing.taxesAndFees, pricing.propertyFees].every(amount => Number.isFinite(amount) && amount >= 0)) {
    return { ...context, result: 'invalid_portal' }
  }

  // Compare currency minor units, not floating point sums or the modal's whole-dollar formatting.
  const digits = new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 2
  const scale = 10 ** digits
  const delta = (actual: number, expected: number) => (Math.round(actual * scale) - Math.round(expected * scale)) / scale
  const totalDifference = delta(pricing.total, sourceTotal)
  const taxDifference = delta(pricing.taxesAndFees, sourceTax)
  const feesDifference = delta(pricing.propertyFees, sourceFees)
  const taxesAndFeesDifference = delta(pricing.taxesAndFees + pricing.propertyFees, sourceTax + sourceFees)
  const totalMismatch = totalDifference !== 0
  const taxMismatch = taxDifference !== 0
  const feesMismatch = feesDifference !== 0

  return {
    ...context,
    result: totalMismatch || taxMismatch || feesMismatch ? 'mismatch' : 'match',
    totalDifference,
    taxDifference,
    feesDifference,
    taxesAndFeesDifference,
    totalMismatch,
    taxMismatch,
    feesMismatch,
  }
}
